import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Plus } from 'lucide-react';
import { NepaliDatePicker } from "nepali-datepicker-reactjs";
import "nepali-datepicker-reactjs/dist/index.css";
import './UnifiedPatientForm.css';

const UnifiedPatientForm = ({ isOpen, onClose, onAddPatient, onUpdatePatient, patient }) => {
  const [formData, setFormData] = useState({
    regNo: '',
    firstName: '',
    lastName: '',
    age: '',
    sex: '',
    fatherName: '',
    motherName: '',
    localAddress: '',
    dobNepali: '',
    phone: '',
    vaccines: []
  });

  React.useEffect(() => {
    if (patient) {
      // Split name back into first and last name if possible, or just put it all in first name
      const nameParts = patient.name ? patient.name.split(' ') : ['', ''];
      setFormData({
        regNo: patient.mrn || '',
        firstName: nameParts[0] || '',
        lastName: nameParts.slice(1).join(' ') || '',
        age: patient.age || '',
        sex: patient.gender || patient.sex || '',
        fatherName: patient.fatherName || '',
        motherName: patient.motherName || '',
        localAddress: patient.localAddress || '',
        dobNepali: patient.dobNepali || '',
        phone: patient.phone || '',
        vaccines: patient.vaccines || []
      });
    } else {
      setFormData({
        regNo: '',
        firstName: '',
        lastName: '',
        age: '',
        sex: '',
        fatherName: '',
        motherName: '',
        localAddress: '',
        dobNepali: '',
        phone: '',
        vaccines: []
      });
    }
  }, [patient, isOpen]);

  const [errors, setErrors] = useState({});



  const validateForm = () => {
    const newErrors = {};

    if (!formData.regNo.trim()) newErrors.regNo = 'Reg. No is required';
    if (!formData.firstName.trim()) newErrors.firstName = 'First Name is required';
    if (!formData.lastName.trim()) newErrors.lastName = 'Last Name is required';

    if (!formData.sex) newErrors.sex = 'Sex is required';
    if (!formData.phone.trim()) newErrors.phone = 'Phone is required';

    setErrors(newErrors);
    console.log('UnifiedPatientForm: Validation errors:', newErrors);
    console.log('UnifiedPatientForm: Current formData:', formData);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    console.log('UnifiedPatientForm: Submit button clicked');

    if (validateForm()) {
      // Combine first and last name for the main 'name' field used in the app
      const patientData = {
        ...formData,
        name: `${formData.firstName} ${formData.lastName}`,
        mrn: formData.regNo, // Mapping Reg No to MRN for compatibility
        // Initialize with empty vaccines array
        vaccines: []
      };

      console.log('UnifiedPatientForm: Submitting patient data:', patientData);
      try {
        if (patient) {
          onUpdatePatient(patientData);
          console.log('UnifiedPatientForm: onUpdatePatient called successfully');
        } else {
          onAddPatient(patientData);
          console.log('UnifiedPatientForm: onAddPatient called successfully');
        }
        resetForm();
        onClose();
      } catch (error) {
        console.error('UnifiedPatientForm: Error calling action:', error);
        alert('Error saving patient data. Please check console.');
      }
    } else {
      console.log('UnifiedPatientForm: Validation failed', errors);
      alert('Please fill in all required fields correctly.');
    }
  };

  const resetForm = () => {
    setFormData({
      regNo: '',
      firstName: '',
      lastName: '',
      age: '',
      sex: '',
      fatherName: '',
      motherName: '',
      localAddress: '',
      dobNepali: '',
      phone: '',
      vaccines: []
    });
    setErrors({});
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };



  const modalVariants = {
    hidden: { opacity: 0, scale: 0.8 },
    visible: { opacity: 1, scale: 1, transition: { type: "spring", damping: 25, stiffness: 300 } },
    exit: { opacity: 0, scale: 0.8, transition: { duration: 0.2 } }
  };

  const overlayVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1 },
    exit: { opacity: 0 }
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        className="modal-overlay"
        variants={overlayVariants}
        initial="hidden"
        animate="visible"
        exit="exit"
        onClick={onClose}
      >
        <motion.div
          className="modal-content unified-modal"
          variants={modalVariants}
          initial="hidden"
          animate="visible"
          exit="exit"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="modal-header">
            <h2>{patient ? 'Edit Patient Details' : 'New Patient Registration'}</h2>
            <motion.button
              className="close-btn"
              onClick={onClose}
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
            >
              <X size={20} />
            </motion.button>
          </div>

          <form onSubmit={handleSubmit} className="unified-form">
            <div className="form-section">
              <h3>Personal Information</h3>
              <div className="form-grid">
                <div className="form-group">
                  <label>Reg. No *</label>
                  <input
                    type="text"
                    value={formData.regNo}
                    onChange={(e) => handleChange('regNo', e.target.value)}
                    className={errors.regNo ? 'error' : ''}
                    placeholder="Registration Number"
                  />
                  {errors.regNo && <span className="error-text">{errors.regNo}</span>}
                </div>

                <div className="form-group">
                  <label>First Name *</label>
                  <input
                    type="text"
                    value={formData.firstName}
                    onChange={(e) => handleChange('firstName', e.target.value)}
                    className={errors.firstName ? 'error' : ''}
                    placeholder="First Name"
                  />
                  {errors.firstName && <span className="error-text">{errors.firstName}</span>}
                </div>

                <div className="form-group">
                  <label>Last Name *</label>
                  <input
                    type="text"
                    value={formData.lastName}
                    onChange={(e) => handleChange('lastName', e.target.value)}
                    className={errors.lastName ? 'error' : ''}
                    placeholder="Last Name"
                  />
                  {errors.lastName && <span className="error-text">{errors.lastName}</span>}
                </div>

                <div className="form-group">
                  <label>Age</label>
                  <input
                    type="text"
                    value={formData.age}
                    onChange={(e) => handleChange('age', e.target.value)}
                    className={errors.age ? 'error' : ''}
                    placeholder="Age"
                  />
                  {errors.age && <span className="error-text">{errors.age}</span>}
                </div>

                <div className="form-group">
                  <label>Sex *</label>
                  <div className="radio-group">
                    <label className={`radio-option ${formData.sex === 'Male' ? 'selected' : ''}`}>
                      <input
                        type="radio"
                        name="sex"
                        value="Male"
                        checked={formData.sex === 'Male'}
                        onChange={(e) => handleChange('sex', e.target.value)}
                      />
                      <span>Male</span>
                    </label>
                    <label className={`radio-option ${formData.sex === 'Female' ? 'selected' : ''}`}>
                      <input
                        type="radio"
                        name="sex"
                        value="Female"
                        checked={formData.sex === 'Female'}
                        onChange={(e) => handleChange('sex', e.target.value)}
                      />
                      <span>Female</span>
                    </label>
                    <label className={`radio-option ${formData.sex === 'Other' ? 'selected' : ''}`}>
                      <input
                        type="radio"
                        name="sex"
                        value="Other"
                        checked={formData.sex === 'Other'}
                        onChange={(e) => handleChange('sex', e.target.value)}
                      />
                      <span>Other</span>
                    </label>
                  </div>
                  {errors.sex && <span className="error-text">{errors.sex}</span>}
                </div>

                <div className="form-group">
                  <label>DOB (Nepali)</label>
                  <div className="nepali-datepicker-container">
                    <NepaliDatePicker
                      inputClassName="form-control"
                      className=""
                      value={formData.dobNepali}
                      onChange={(value) => handleChange('dobNepali', value)}
                      options={{ calenderLocale: "ne", valueLocale: "en" }}
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label>Phone (+977) *</label>
                  <input
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => handleChange('phone', e.target.value)}
                    className={errors.phone ? 'error' : ''}
                    placeholder="98XXXXXXXX"
                  />
                  {errors.phone && <span className="error-text">{errors.phone}</span>}
                </div>
              </div>
            </div>

            <div className="form-section">
              <h3>Family & Address</h3>
              <div className="form-grid">
                <div className="form-group">
                  <label>Father's Name</label>
                  <input
                    type="text"
                    value={formData.fatherName}
                    onChange={(e) => handleChange('fatherName', e.target.value)}
                    placeholder="Father's Name"
                  />
                </div>

                <div className="form-group">
                  <label>Mother's Name</label>
                  <input
                    type="text"
                    value={formData.motherName}
                    onChange={(e) => handleChange('motherName', e.target.value)}
                    placeholder="Mother's Name"
                  />
                </div>

                <div className="form-group full-width">
                  <label>Local Address</label>
                  <input
                    type="text"
                    value={formData.localAddress}
                    onChange={(e) => handleChange('localAddress', e.target.value)}
                    placeholder="Street, City, District"
                  />
                </div>
              </div>
            </div>



            <div className="form-actions">
              <motion.button
                type="button"
                className="btn-cancel"
                onClick={onClose}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                Cancel
              </motion.button>
              <motion.button
                type="submit"
                className="btn-submit"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <Plus size={18} />
                {patient ? 'Update Patient' : 'Register Patient'}
              </motion.button>
            </div>
          </form>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default UnifiedPatientForm;
